import { TIMED_SERIES_VERSION, timedSeriesView } from './69-timed-series.mjs';
import { getGame } from './49-game-catalog.mjs';
import { createIdle, moveIdle, idleView } from './51-idle-games.mjs';
import { createScore, moveScore, scoreView } from './52-score-games.mjs';
import { GAME_MOVE_LIMIT, reject } from './53-game-primitives.mjs';
export { GAME_MOVE_LIMIT } from './53-game-primitives.mjs';
export const GAME_ENGINE_VERSION = 'visual-toys-2.4.0';

/** Initialize a closed game identity with a uint32 seed. Positions, refill and
 * subsequent rounds are reproducible. State is bounded JSON, not a permission,
 * elapsed-time or human-play proof. Server score verification replays moves from
 * its own seed; it must never accept a caller-supplied state or points. */
export function createGame(id, seed = 1) {
  const game = getGame(id);
  if (!game || game.companionOnly || !Number.isInteger(seed) || seed < 0 || seed > 0xffffffff)
    reject('Unknown game or invalid uint32 seed.');
  const state = {
    version: GAME_ENGINE_VERSION,
    gameId: id,
    seed,
    rng: seed,
    moves: 0,
    score: 0,
    completed: false,
    data: {},
  };
  if (game.kind === 'idle') createIdle(state);
  else createScore(state);
  return state;
}

/** Derive a tap/keyboard renderer contract. Disabled cells have no legal action;
 * decorative cells omit move. No visible game requires colour perception alone. */
export function gameView(state) {
  if (state?.version === TIMED_SERIES_VERSION) return timedSeriesView(state);
  const game = getGame(state?.gameId);
  if (!game || state.version !== GAME_ENGINE_VERSION) reject('Unknown game state version.');
  const view = game.kind === 'idle' ? idleView(state) : scoreView(state);
  const moveLimitReached = state.moves >= GAME_MOVE_LIMIT;
  if (state.completed || moveLimitReached) {
    view.stageComplete = false;
    view.advanceAction = null;
    view.autoDismiss = null;
    view.completionReason = state.completed
      ? view.completionReason || (state.data.completion === 'creation' ? 'creation' : 'solved')
      : 'move-limit';
    view.status = {
      creation: 'Creation complete.',
      solved: 'Puzzle complete.',
      'goal-reached': 'Goal reached.',
      'round-complete': 'Round complete.',
      stalled: 'No legal moves remain. Round finished.',
      'move-limit': 'Move limit reached. Round finished.',
    }[view.completionReason];
    view.cells = view.cells.map((cell) => ({ ...cell, disabled: true }));
    view.options = [];
  }
  return {
    ...view,
    title: game.title,
    instructions: game.instructions,
    kind: game.kind,
    score: state.score,
    completed: state.completed,
    moveLimitReached,
  };
}

/** Apply exactly {type:'cell'|'option',index:integer} immutably. Invalid actions
 * throw RangeError/code invalid_game_move without changing input. At most600
 * accepted actions are allowed per session; the server additionally owns120s
 * scoring expiry. Creative idle toys never masquerade as scored challenges. */
export function applyGameMove(state, move) {
  const game = getGame(state?.gameId);
  if (
    !game ||
    state.version !== GAME_ENGINE_VERSION ||
    !Number.isInteger(state.moves) ||
    state.moves < 0 ||
    state.moves >= GAME_MOVE_LIMIT ||
    state.completed
  )
    reject('This game session is not accepting moves.');
  if (
    !move ||
    typeof move !== 'object' ||
    Array.isArray(move) ||
    Object.keys(move).length !== 2 ||
    !['cell', 'option'].includes(move.type) ||
    !Number.isInteger(move.index) ||
    move.index < 0
  )
    reject('Malformed game move.');
  const view = gameView(state);
  const action = (move.type === 'cell' ? view.cells : view.options)[move.index];
  if (
    !action ||
    action.disabled ||
    !action.move ||
    action.move.type !== move.type ||
    action.move.index !== move.index
  )
    reject();
  const result = structuredClone(state);
  if (game.kind === 'idle') moveIdle(result, move);
  else moveScore(result, move);
  result.moves++;
  if (result.completed || result.moves >= GAME_MOVE_LIMIT) {
    const reason = gameView(result).completionReason;
    const events = result.data.events || [];
    result.data.events = events.some((event) => event.type === 'complete')
      ? events.map((event) =>
          event.type === 'complete' ? { ...event, reason: event.reason || reason } : event,
        )
      : [...events, { type: 'complete', reason, points: 0 }];
  }
  if (
    !Number.isSafeInteger(result.score) ||
    result.score < 0 ||
    JSON.stringify(result).length > 20000
  )
    reject('Game state exceeded its bounds.');
  return result;
}

/** Hint is a descriptive goal/status, not an automatic solver or scored move. */
export function gameHint(state) {
  const view = gameView(state);
  return view.status || view.goal || view.instructions;
}
